import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { getMedalImageUrl } from '../../utils/formatters';
import { currentLocale } from '../../i18n';
import ImageLightbox from '../common/ImageLightbox';
import TournamentBracket from './TournamentBracket';
import TournamentComments from './TournamentComments';
import TournamentMarket from './TournamentMarket';
import TournamentPrizePool from './TournamentPrizePool';
import PlayerPicker from './PlayerPicker';
import './Tournaments.css';

// Status labels are translated; keys resolved via t() at render time
// (module-level constant can't call hooks).
const STATUS_LABELS = {
  draft: 'tournament.statusDraft',
  scheduled: 'tournament.statusScheduled',
  registration_open: 'tournament.statusRegOpen',
  registration_closed: 'tournament.statusRegClosed',
  in_progress: 'tournament.statusInProgress',
  completed: 'tournament.statusCompleted',
  cancelled: 'tournament.statusCancelled',
};

const STATUS_COLORS = {
  draft: 'var(--text-muted)',
  scheduled: 'var(--accent-blue)',
  registration_open: 'var(--accent-green)',
  registration_closed: 'var(--accent-orange)',
  in_progress: 'var(--accent-blue)',
  completed: '#9C27B0',
  cancelled: 'var(--accent-red)',
};

/**
 * Team registration form for 2v2 and 5v5 tournaments with teammate picker.
 */
function TeamRegistrationForm({ tournament, userDiscordId, onRegister, loading }) {
  const { t } = useTranslation();
  const [teamName, setTeamName] = useState('');
  const [teammates, setTeammates] = useState([]);

  const neededTeammates = tournament.team_size - 1;
  const canSubmit = teamName.trim() && teammates.length === neededTeammates && !loading;

  return (
    <div className="tournament-team-form">
      <div>
        <label>{t('tournament.teamNameLabel')}</label>
        <input
          type="text"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          placeholder={t('tournament.teamNamePlaceholder')}
          className="tournament-team-input"
        />
      </div>

      <PlayerPicker
        guildId={tournament.guild_id}
        selected={teammates}
        onChange={setTeammates}
        maxPlayers={neededTeammates}
        excludeDiscordIds={userDiscordId ? [userDiscordId] : []}
        label={t('tournament.pickTeammates', { count: neededTeammates })}
      />

      <div className="tournament-team-submit">
        <button
          onClick={() => onRegister({ team_name: teamName, teammate_ids: teammates })}
          disabled={!canSubmit}
          className="tournament-btn-register"
        >
          {loading ? t('tournament.registering') : t('tournament.registerTeam', { size: tournament.team_size })}
        </button>
        {tournament.entry_fee_points > 0 && (
          <span className="tournament-team-hint">
            {t('prizePool.entryFee', { fee: tournament.entry_fee_points })}
          </span>
        )}
        {!canSubmit && teamName.trim() && (
          <span className="tournament-team-hint">
            {t('tournament.pickMore', { count: neededTeammates - teammates.length })}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Public tournament detail page with bracket, registrations and registration form.
 */
export default function TournamentDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, getAuthHeaders, user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [medals, setMedals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regLoading, setRegLoading] = useState(false);
  const [regMsg, setRegMsg] = useState('');
  const [lightboxMedal, setLightboxMedal] = useState(null);
  // Räknas upp efter registrering/avanmälan så prispotten hämtas om.
  const [poolRefresh, setPoolRefresh] = useState(0);

  const fetchTournament = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${id}`);
      if (resp.ok) setTournament(await resp.json());
    } catch (e) {
      console.error('Failed to fetch tournament:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchRegistrations = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${id}/registrations`);
      if (resp.ok) setRegistrations(await resp.json());
    } catch (e) { /* silent */ }
  }, [id]);

  const fetchPrizes = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${id}/prizes`);
      if (resp.ok) setPrizes(await resp.json());
    } catch (e) { /* silent */ }
  }, [id]);

  const fetchPlacements = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${id}/placements`);
      if (resp.ok) setPlacements(await resp.json());
    } catch (e) { /* silent */ }
  }, [id]);

  const fetchMedals = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${id}/medals`);
      if (resp.ok) setMedals(await resp.json());
    } catch (e) { /* silent */ }
  }, [id]);

  useEffect(() => {
    fetchTournament();
    fetchRegistrations();
    fetchPrizes();
    fetchPlacements();
    fetchMedals();
  }, [fetchTournament, fetchRegistrations, fetchPrizes, fetchPlacements, fetchMedals]);

  /**
   * Register for the tournament with optional team data.
   */
  const handleRegister = async (data = {}) => {
    setRegLoading(true);
    setRegMsg('');
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${id}/register`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (resp.ok) {
        setRegMsg(t('tournament.msgRegistered'));
        fetchTournament();
        fetchRegistrations();
        setPoolRefresh((n) => n + 1);
        window.dispatchEvent(new CustomEvent('wallet-refresh'));
      } else {
        const err = await resp.json();
        setRegMsg(err.error || err.detail || t('tournament.regFailed'));
      }
    } catch (e) {
      setRegMsg(t('tournament.networkError'));
    } finally {
      setRegLoading(false);
    }
  };

  /**
   * Unregister from the tournament.
   */
  const handleUnregister = async () => {
    setRegLoading(true);
    setRegMsg('');
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${id}/unregister`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        setRegMsg(t('tournament.msgUnregistered'));
        fetchTournament();
        fetchRegistrations();
        setPoolRefresh((n) => n + 1);
        window.dispatchEvent(new CustomEvent('wallet-refresh'));
      } else {
        const err = await resp.json();
        setRegMsg(err.error || err.detail || t('tournament.unregFailed'));
      }
    } catch (e) {
      setRegMsg(t('tournament.networkError'));
    } finally {
      setRegLoading(false);
    }
  };

  /**
   * Check in for the tournament.
   */
  const handleCheckin = async () => {
    setRegLoading(true);
    setRegMsg('');
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${id}/checkin`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (resp.ok) {
        setRegMsg(t('tournament.msgCheckedIn'));
        fetchRegistrations();
      } else {
        const err = await resp.json();
        setRegMsg(err.error || err.detail || t('tournament.checkinFailed'));
      }
    } catch (e) {
      setRegMsg(t('tournament.networkError'));
    } finally {
      setRegLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="tournament-page" style={{ textAlign: 'center', paddingTop: '3rem' }}>
        <h2>{t('tournament.notFound')}</h2>
        <button onClick={() => navigate('/tournaments')} className="tournament-btn-register" style={{ marginTop: 16 }}>
          {t('profile.back')}
        </button>
      </div>
    );
  }

  const isOpen = tournament.status === 'registration_open';
  const userReg = registrations.find((r) => r.player_discord_id === user?.discord_id && r.status !== 'withdrawn');
  const userRegistered = !!userReg;
  const userCheckedIn = userReg?.checked_in;
  const isTeamMode = tournament.team_size > 1;
  const needsCheckin = tournament.check_in_required && userRegistered && !userCheckedIn;

  return (
    <div className="tournament-page">
      {/* Header */}
      <div className="tournament-detail-header">
        <button onClick={() => navigate('/tournaments')} className="tournament-back-btn">
          &larr; {t('tournament.allTournaments')}
        </button>
        <h1 className="tournament-detail-title">{tournament.name}</h1>
        <div className="tournament-detail-meta">
          <span className="status" style={{ color: STATUS_COLORS[tournament.status] }}>
            {STATUS_LABELS[tournament.status] ? t(STATUS_LABELS[tournament.status]) : tournament.status}
          </span>
          <span>{tournament.team_size}v{tournament.team_size}</span>
          <span>{tournament.format === 'double_elim' ? t('tournament.doubleElimFull') : t('tournament.singleElimFull')}</span>
          <span>{tournament.match_type}</span>
          <span>{t('tournament.registered', { count: tournament.registration_count, max: tournament.max_slots })}</span>
          {tournament.affects_elo && <span className="ranked">{t('tournament.ranked')}</span>}
        </div>
        {(tournament.starts_at || tournament.registration_opens_at || tournament.registration_closes_at) && (
          <div className="tournament-detail-dates">
            {tournament.starts_at && (
              <span>{t('tournament.startPrefix', { date: new Date(tournament.starts_at).toLocaleString(currentLocale()) })}</span>
            )}
            {tournament.registration_opens_at && (
              <span>{t('tournament.regOpensPrefix', { date: new Date(tournament.registration_opens_at).toLocaleString(currentLocale()) })}</span>
            )}
            {tournament.registration_closes_at && (
              <span>{t('tournament.regClosesPrefix', { date: new Date(tournament.registration_closes_at).toLocaleString(currentLocale()) })}</span>
            )}
          </div>
        )}
        {tournament.description && (
          <p className="tournament-detail-desc">{tournament.description}</p>
        )}
        {tournament.rules && (
          <div className="tournament-rules">
            <h3 className="tournament-rules-title">{t('tournament.rules')}</h3>
            <pre className="tournament-rules-text">{tournament.rules}</pre>
          </div>
        )}
      </div>

      {/* Scheduled — show when registration opens */}
      {tournament.status === 'scheduled' && tournament.registration_opens_at && (
        <div className="tournament-reg-box">
          <span className="tournament-reg-status">
            {t('tournament.registrationOpensAt', { date: new Date(tournament.registration_opens_at).toLocaleString(currentLocale()) })}
          </span>
        </div>
      )}

      {/* Registration action */}
      {isLoggedIn && isOpen && (
        <div className="tournament-reg-box">
          {userRegistered ? (
            <div className="tournament-reg-actions">
              <span className="tournament-reg-status">
                {userCheckedIn ? t('tournament.registeredCheckedIn') : t('tournament.youAreRegistered')}
              </span>
              {needsCheckin && (
                <button onClick={handleCheckin} disabled={regLoading} className="tournament-btn-checkin">
                  {regLoading ? '...' : t('tournament.checkIn')}
                </button>
              )}
              <button onClick={handleUnregister} disabled={regLoading} className="tournament-btn-unregister">
                {regLoading ? '...' : t('tournament.unregister')}
              </button>
            </div>
          ) : isTeamMode ? (
            <TeamRegistrationForm
              tournament={tournament}
              userDiscordId={user?.discord_id}
              onRegister={handleRegister}
              loading={regLoading}
            />
          ) : (
            <div className="tournament-reg-actions">
              <button onClick={() => handleRegister()} disabled={regLoading} className="tournament-btn-register">
                {regLoading
                  ? t('tournament.registering')
                  : tournament.entry_fee_points > 0
                    ? t('prizePool.registerWithFee', { fee: tournament.entry_fee_points })
                    : t('tournament.registerSelf')}
              </button>
              <span className="tournament-reg-hint">
                {t('tournament.slotsFilled', { count: tournament.registration_count, max: tournament.max_slots })}
              </span>
            </div>
          )}
          {regMsg && <div className="tournament-reg-msg">{regMsg}</div>}
        </div>
      )}

      {/* Prispott + turneringsmarknad (A1 + A2) */}
      <TournamentPrizePool tournamentId={id} refreshKey={poolRefresh} />
      <TournamentMarket tournamentId={id} />

      {/* Placements (completed tournaments) */}
      {placements.length > 0 && (
        <div className="tournament-placements">
          <h2 className="tournament-section-title">{t('tournament.results')}</h2>
          <div className="tournament-placement-list">
            {placements.map((p) => {
              const tierClass = p.placement === 1 ? 'gold' : p.placement === 2 ? 'silver' : p.placement === 3 ? 'bronze' : '';
              const medal = medals.find(m => m.placement === p.placement);
              return (
                <div key={p.placement} className={`tournament-placement-item ${tierClass}`}>
                  {medal && (
                    <img
                      src={getMedalImageUrl(medal.image_url)}
                      alt={t('tournament.medalAlt', { place: p.placement })}
                      className="tournament-medal-img clickable"
                      onClick={() => setLightboxMedal({ ...medal, display_name: p.display_name })}
                    />
                  )}
                  <span className={`tournament-placement-rank ${tierClass || 'other'}`}>
                    #{p.placement}
                  </span>
                  <span className={`tournament-placement-name ${p.placement <= 3 ? 'top3' : ''}`}>
                    {p.display_name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Prizes */}
      {prizes.length > 0 && (
        <div className="tournament-prizes">
          <h2 className="tournament-section-title">{t('tournament.prizes')}</h2>
          <div className="tournament-prize-list">
            {prizes.map((p) => (
              <div key={p.id} className="tournament-prize-card">
                <div className="tournament-prize-placement">#{p.placement}</div>
                {p.skin_image_url && (
                  <img src={p.skin_image_url} alt={p.skin_name} className="tournament-prize-img" />
                )}
                <div className="tournament-prize-name">{p.skin_name || p.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bracket */}
      {tournament.bracket && tournament.bracket.length > 0 && (
        <div className="tournament-bracket-section">
          <h2 className="tournament-section-title">{t('tournament.bracketTitle')}</h2>
          <TournamentBracket tournamentId={id} initialBracket={tournament.bracket} />
        </div>
      )}

      {/* Registrations */}
      <div className="tournament-registrations">
        <h2 className="tournament-section-title">
          {t('tournament.registeredCount', { count: registrations.length })}
        </h2>
        {registrations.length === 0 ? (
          <p className="tournament-reg-hint">{t('tournament.noneRegistered')}</p>
        ) : (
          <div className="tournament-reg-list">
            {registrations.map((r, i) => (
              <div key={r.id} className="tournament-reg-item">
                <div className="tournament-reg-item-row">
                  <span className="tournament-reg-seed">{r.seed || i + 1}.</span>
                  <span className="tournament-reg-name">{r.display_name || r.team_name}</span>
                  {r.status === 'confirmed' && (
                    <span className="tournament-reg-confirmed">{t('tournament.confirmed')}</span>
                  )}
                </div>
                {isTeamMode && r.roster && r.roster.length > 0 && (
                  <div className="tournament-reg-roster">
                    {r.roster.map((p, pi) => (
                      <span key={pi} className="tournament-reg-roster-player">
                        {p.steam_username || p.discord_username || t('tournament.unknownPlayer')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Comments */}
      <TournamentComments tournamentId={id} />

      {lightboxMedal && (
        <ImageLightbox
          src={getMedalImageUrl(lightboxMedal.image_url)}
          title={t('tournament.medalLightboxTitle', {
            name: tournament.name,
            place: t({ 1: 'tournament.place1', 2: 'tournament.place2', 3: 'tournament.place3' }[lightboxMedal.placement] || 'tournament.place1'),
          })}
          subtitle={lightboxMedal.display_name || ''}
          onClose={() => setLightboxMedal(null)}
        />
      )}
    </div>
  );
}
